import { z } from "zod";

/**
 * Base Connection schema matching the API response
 * (internally uses the same v4 authentications API)
 */
export const ConnectionSchema = z.object({
  id: z.number().describe("Unique identifier for the connection"),
  date: z.string().describe("Date created"),
  lastchanged: z.string().optional().describe("Date last changed"),
  account_id: z.number().describe("Account ID associated with this connection"),
  customuser_id: z
    .number()
    .optional()
    .describe("Custom user ID (if applicable)"),
  selected_api: z.string().describe("Selected API key (internal identifier)"),
  destination_selected_api: z
    .string()
    .nullable()
    .optional()
    .describe("Destination API key (if applicable)"),
  is_invite_only: z.boolean().describe("Whether the connection is invite-only"),
  is_private: z.boolean().describe("Whether the connection is private"),
  shared_with_all: z
    .boolean()
    .describe("Whether the connection is shared with all users"),
  is_stale: z.string().optional().describe("Stale status string"),
  is_shared: z.string().optional().describe("Shared status string"),
  marked_stale_at: z
    .string()
    .nullable()
    .optional()
    .describe("Date when marked stale"),
  label: z
    .string()
    .nullable()
    .optional()
    .describe("User label for the connection"),
  identifier: z.string().nullable().optional().describe("Identifier"),
  title: z.string().nullable().optional().describe("Title of the connection"),
  url: z.string().optional().describe("URL to the connection resource"),
  groups: z
    .array(
      z
        .record(z.string(), z.unknown())
        .describe("Groups associated with the connection"),
    )
    .optional()
    .describe("Array of groups associated with the connection"),
  members: z
    .string()
    .optional()
    .describe("Members associated with the connection"),
  permissions: z
    .record(z.string(), z.boolean())
    .optional()
    .describe("Permissions for the connection"),
  public_id: z.string().optional().describe("Public UUID for the connection"),
  account_public_id: z
    .string()
    .optional()
    .describe("Public UUID for the associated account"),
  customuser_public_id: z
    .string()
    .optional()
    .describe("Public UUID for the associated custom user"),
});

/**
 * Normalized connection item returned by getConnection handler
 *
 * Transforms API response fields:
 * - selected_api → implementation_id
 * - customuser_id → profile_id
 * - is_stale → is_expired (preserved as is_stale too)
 * - marked_stale_at → expired_at (preserved as marked_stale_at too)
 *
 * Adds computed fields:
 * - app_key: Extracted from selected_api (e.g., "SlackCLIAPI@1.0.0" → "SlackCLIAPI")
 * - app_version: Extracted from selected_api (e.g., "SlackCLIAPI@1.0.0" → "1.0.0")
 */
export const ConnectionItemSchema = ConnectionSchema.omit({
  selected_api: true,
  customuser_id: true,
}).extend({
  // Override numeric IDs with string versions (converted by normalizeConnectionItem)
  id: z.string().describe("Unique identifier for the connection"),
  account_id: z.string().describe("Account ID associated with this connection"),

  // Renamed fields
  implementation_id: z
    .string()
    .optional()
    .describe("Implementation ID (was selected_api)"),
  profile_id: z.string().optional().describe("Profile ID (was customuser_id)"),

  // Mapped fields (originals preserved in ...restOfAuth)
  is_expired: z
    .string()
    .optional()
    .describe("Whether the connection is expired (mapped from is_stale)"),
  expired_at: z
    .string()
    .nullable()
    .optional()
    .describe("Date when connection expired (mapped from marked_stale_at)"),

  // Computed fields
  app_key: z
    .string()
    .optional()
    .describe("App Key extracted from implementation_id"),
  app_version: z
    .string()
    .optional()
    .describe("App Version extracted from implementation_id"),
});

export const ConnectionsResponseSchema = z.object({
  count: z.number().describe("Total number of items"),
  next: z
    .string()
    .nullable()
    .optional()
    .describe("Cursor for the next page of results (if available)"),
  previous: z
    .string()
    .nullable()
    .optional()
    .describe("Cursor for the previous page of results (if available)"),
  results: z.array(ConnectionSchema).describe("Array of connection items"),
});

export type Connection = z.infer<typeof ConnectionSchema>;
export type ConnectionItem = z.infer<typeof ConnectionItemSchema>;
export type ConnectionsResponse = z.infer<typeof ConnectionsResponseSchema>;

/**
 * Path parameters schema for getConnection endpoint.
 */
export const GetConnectionParamSchema = z
  .object({
    connectionId: z.string().describe("Connection ID to retrieve"),
  })
  .describe("Get a specific connection by ID");

export type GetConnectionParam = z.infer<typeof GetConnectionParamSchema>;

/**
 * Response type for getConnection endpoint.
 * Wraps ConnectionItem in the standard API response envelope.
 */
export type GetConnectionResponse = { data: ConnectionItem };

/**
 * Query parameters for listing connections
 */
export const ListConnectionsQuerySchema = z
  .object({
    app_key: z
      .string()
      .optional()
      .describe(
        "Filter connections by app key (e.g., 'SlackCLIAPI' or slug like 'github')",
      ),
    connection_ids: z
      .string()
      .optional()
      .describe("Comma-separated list of connection IDs to filter by"),
    search: z
      .string()
      .optional()
      .describe("Search term to filter connections by title"),
    title: z
      .string()
      .optional()
      .describe(
        "Filter connections by exact title match (searches first, then filters locally)",
      ),
    account_id: z
      .string()
      .optional()
      .describe("Filter connections by account ID"),
    owner: z
      .string()
      .optional()
      .describe(
        "Filter by owner, 'me' for your own connections or a specific user ID",
      ),
    is_expired: z
      .boolean()
      .optional()
      .describe(
        "Filter by expired status (true = expired only, false = non-expired only)",
      ),
    page_size: z
      .number()
      .min(1)
      .optional()
      .describe("Number of connections per page"),
    offset: z
      .string()
      .optional()
      .describe("Pagination offset from previous response"),
  })
  .describe("Query parameters for listing connections");

/**
 * Response schema for listConnections
 */
export const ListConnectionsResponseSchema = z
  .object({
    data: z.array(ConnectionItemSchema).describe("Array of connection items"),
    links: z
      .object({
        next: z
          .string()
          .nullable()
          .optional()
          .describe(
            "Fully qualified URL for the next page of results (if available), e.g. https://sdkapi.zapier.com/api/v0/connections?offset=100&page_size=50",
          ),
      })
      .describe("Pagination links for navigating through results"),
    meta: z
      .object({
        count: z.number().describe("Total number of items"),
        limit: z.number().describe("Number of items per page"),
        offset: z.number().describe("Offset of the current page"),
      })
      .describe("Metadata for the paginated result set"),
  })
  .describe("Response schema for listing connections");

/**
 * TypeScript types for request and response
 */
export type ListConnectionsQuery = z.infer<typeof ListConnectionsQuerySchema>;
export type ListConnectionsResponse = z.infer<
  typeof ListConnectionsResponseSchema
>;
